import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UrlChangeMessage } from "../../../shared/liveBrowserRuntime/protocol";
import { publicRouteUrl } from "../../../shared/publicUrl";
import { apiUrl, publicDomain, publicUrl } from "../../lib/tenantOrigin";
import type { LiveFeature } from "./feature";

// Routing the frame through verify-route mints the space-scoped
// `__3b_session` cookie on the page's origin before it loads, so the
// page's same-origin requests arrive authenticated — matching how the
// page behaves when served directly.
function sessionHandshakeUrl(
  redirect: string,
  spaceId: string,
  externalApiUrl: string
): string {
  const url = new URL("/auth/verify-route", externalApiUrl);
  url.searchParams.set("scope", "space");
  url.searchParams.set("spaceId", spaceId);
  url.searchParams.set("redirect", redirect);
  return url.toString();
}

const FrameStack = styled("div")`
  position: relative;
  width: 100%;
  height: 100%;
`;

// A loading frame sits behind the one on screen, at full size and full opacity
// rather than hidden: browsers throttle rendering in frames they treat as
// invisible, and it has to be painted before the swap or it would flash in.
// The frame in front is opaque and covers it exactly.
const StyledIframe = styled("iframe")`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
  background: var(--surface-primary);
  z-index: 1;

  &[data-pending] {
    z-index: 0;
    pointer-events: none;
  }
`;

// allow-same-origin with allow-scripts nullifies the sandbox attribute as a
// boundary; the real isolation here is the origin split (space origin vs UI
// origin), identical to the page open in its own tab. The attribute's only
// residual effect is blocking popup sandbox escape.
const sandbox =
  "allow-scripts allow-same-origin allow-forms allow-popups allow-downloads";

type Frame = { id: number; src: string };
type Frames = { live: Frame; pending: Frame | null };

type StepPageProps = {
  spaceSlug: string;
  spaceId: string | null;
  branchId: string | null;
  publicRoute: string;
  externalId: string | null;
  reloadKey?: string;
  features: readonly LiveFeature[];
};

// The step's page, live in the virtual browser: the iframe points at the
// route as actually served, so what the user sees is what the origin
// returns right now. Feature attach (inspect / agent control / URL
// reporting) receives a MessagePort posted by the runtime bundle that the
// public service injects when the request carries a valid signed
// `__3b_live_browser_runtime` cookie (minted during the `__3b_auth`
// exchange when the frame is embedded in our UI).
//
// A change to `reloadKey` — the step's files settling, or the reload button —
// reloads the page the way a hot reload does: the running document stays on
// screen and interactive while a second frame loads the page the user is
// actually on, and the two swap once that frame has loaded.
export function Page({
  spaceSlug,
  spaceId,
  branchId,
  publicRoute,
  externalId,
  reloadKey = "",
  features,
}: StepPageProps) {
  const externalPublicUrl = publicUrl();
  const externalApiUrl = apiUrl();
  const domain = publicDomain();

  const src = useMemo(
    () =>
      publicRouteUrl(
        spaceSlug,
        publicRoute,
        {
          publicUrl: externalPublicUrl,
          publicWildcardDomain: domain.wildcardDomain,
          combinedSpaceTenantLabel: domain.combinedSpaceTenantLabel,
        },
        { branchId, externalId }
      ),
    [spaceSlug, publicRoute, branchId, externalId, externalPublicUrl, domain]
  );

  const previewOrigin = useMemo(() => new URL(src).origin, [src]);

  const entryUrl = useCallback(
    (target: string) =>
      spaceId ? sessionHandshakeUrl(target, spaceId, externalApiUrl) : target,
    [spaceId, externalApiUrl]
  );

  const [frames, setFrames] = useState<Frames>(() => ({
    live: { id: 0, src: entryUrl(src) },
    pending: null,
  }));
  // The message handler reads the frames a message fired against, so state and
  // ref move together rather than a render apart.
  const framesRef = useRef(frames);
  const nextIdRef = useRef(1);
  const commit = useCallback((next: Frames) => {
    framesRef.current = next;
    setFrames(next);
  }, []);

  const elementsRef = useRef(new Map<number, HTMLIFrameElement>());
  const windowOf = useCallback(
    (frame: Frame) => elementsRef.current.get(frame.id)?.contentWindow ?? null,
    []
  );

  const portRef = useRef<MessagePort | null>(null);
  const pendingPortRef = useRef<MessagePort | null>(null);
  const cleanupsRef = useRef<(() => void)[]>([]);
  const featuresRef = useRef(features);
  featuresRef.current = features;
  const livePathRef = useRef<string | null>(null);

  // Each document's runtime posts one port when its `<script defer>` runs;
  // whichever frame is on screen owns the features.
  const attach = useCallback((port: MessagePort | null) => {
    portRef.current = port;
    if (!port) return;

    // Where a reload should land: the path as the document actually has it,
    // including anything the page's own router pushed.
    const onMessage = (event: MessageEvent) => {
      const data = event.data as UrlChangeMessage | undefined;
      if (data?.type !== "3b-vb-url" || typeof data.path !== "string") return;
      livePathRef.current = data.path;
    };
    port.addEventListener("message", onMessage);
    cleanupsRef.current.push(() =>
      port.removeEventListener("message", onMessage)
    );

    for (const feature of featuresRef.current) {
      const cleanup = feature.attach(port);
      if (cleanup) cleanupsRef.current.push(cleanup);
    }
    port.start();
  }, []);

  const detach = useCallback(() => {
    for (const cleanup of cleanupsRef.current) cleanup();
    cleanupsRef.current = [];
    portRef.current?.close();
    portRef.current = null;
  }, []);

  const show = useCallback(
    (frame: Frame) => {
      const port = pendingPortRef.current;
      pendingPortRef.current = null;
      detach();
      commit({ live: frame, pending: null });
      attach(port);
    },
    [attach, detach, commit]
  );

  const load = useCallback(
    (target: string) => {
      pendingPortRef.current?.close();
      pendingPortRef.current = null;
      commit({
        live: framesRef.current.live,
        pending: { id: nextIdRef.current++, src: entryUrl(target) },
      });
    },
    [commit, entryUrl]
  );

  const srcRef = useRef(src);
  const reloadKeyRef = useRef(reloadKey);
  useEffect(() => {
    const navigated = srcRef.current !== src;
    const reloaded = reloadKeyRef.current !== reloadKey;
    srcRef.current = src;
    reloadKeyRef.current = reloadKey;
    if (navigated) {
      livePathRef.current = null;
      load(src);
      return;
    }
    if (!reloaded) return;
    const path = livePathRef.current;
    load(path ? new URL(path, previewOrigin).toString() : src);
  }, [src, reloadKey, previewOrigin, load]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== "3b-preview-port") return;
      if (event.origin !== previewOrigin) return;
      const port = event.ports[0];
      if (!port) return;

      const { live, pending } = framesRef.current;
      if (pending && event.source === windowOf(pending)) {
        // Held until the frame is shown: starting the port now would drain the
        // document's queued messages before any feature is listening.
        pendingPortRef.current?.close();
        pendingPortRef.current = port;
        return;
      }
      if (event.source !== windowOf(live)) return;
      // The live document navigated itself — a link click, or its own
      // location.reload — so there's nothing held back to swap in.
      detach();
      attach(port);
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      detach();
      pendingPortRef.current?.close();
      pendingPortRef.current = null;
    };
  }, [previewOrigin, attach, detach, windowOf]);

  const rendered = frames.pending
    ? [frames.live, frames.pending]
    : [frames.live];

  return (
    <FrameStack>
      {rendered.map((frame) => (
        <StyledIframe
          key={frame.id}
          ref={(element: HTMLIFrameElement | null) => {
            if (element) elementsRef.current.set(frame.id, element);
            else elementsRef.current.delete(frame.id);
          }}
          src={frame.src}
          sandbox={sandbox}
          data-pending={frame.id === frames.pending?.id ? "" : undefined}
          tabIndex={frame.id === frames.pending?.id ? -1 : undefined}
          onLoad={() => {
            if (framesRef.current.pending?.id === frame.id) show(frame);
          }}
        />
      ))}
    </FrameStack>
  );
}
